import { useState, useEffect, useContext } from "react";
import { DataContext } from "contexts/DataContext";
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import { styled } from '@mui/material/styles';
import {useGridApiRef} from '@mui/x-data-grid';
import { 
  DataGrid, 
  GridToolbar,
  GridColDef, 
  GridRowModesModel, 
  GridToolbarContainer, 
  GridEventListener,
  GridRowEditStopReasons,
  GridRowsProp,
  GridRowId,
  GridRowModes,
  GridRowModel,
  GridActionsCellItem
} from '@mui/x-data-grid';

interface GridProps {
  name?: string;
  columns: GridColDef[];
  editControls?: boolean;
  details?: React.ComponentType<{
    row: any;
    master?: any;
    setDrawerOpen: (open: boolean) => void;
  }>;
  detailsWidth?: string;
  master?: any;
  get?: (row?:any) => Promise<any>;
  update?: (row: any,masterRow?: any) => Promise<any>;
  delete?: (id: any, masterRow?: any) => Promise<any>;
  labels?: any;
  reloadParentOnEditStop?: boolean;
  addButtonRef?: any;
  hideAddButton?: boolean;
  hideDeleteButton?: boolean;
  showGridToolbar?: boolean;
  initialPageSize?: number;
  filterModel?: any;
  group?:any;
  columnVisibility?: any;
}
const StyledBox = styled('div')(({ theme }) => ({
  minHeight: 50,
  width: '100%',
  backgroundColor: theme.palette.background.default,
  '& .Mui-error': {
    backgroundColor: `rgb(126,10,15,0.5)`,
    color: '#fff',
  },
  '& .MuiDataGrid-cell:focus-within:has(.Mui-error)': {
    outline: `1px solid ${theme.palette.error.main}`,
  }
}));

function EditableGrid (gridProps: GridProps) {
  const apiRef = useGridApiRef();

  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(0 as GridRowId);
  const [selectedItem, setSelectedItem] = useState({} as any);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [rows, setRows] = useState([] as any[]);
  const [currentPage, setCurrentPage] = useState();

  const dataContext = useContext(DataContext);
  const editControls = gridProps.editControls!==undefined ? gridProps.editControls : true;
  const get = gridProps.get || (() => Promise.resolve([]));
  const update = gridProps.update || (() => Promise.resolve());
  const destroy = gridProps.delete || (() => Promise.resolve());

  useEffect(() => {
    dataContext.changeShowLoader(true);
    setLoading(true);
    if(dataContext.loading) return;

    get(gridProps.master).then((data:[]) => {
      setRows(data);
      setLoading(false);
      dataContext.changeShowLoader(false);
    })
      // eslint-disable-next-line
  }, [dataContext.loading, dataContext.reloadParent])

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      //event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: columns[1].field  } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteConfirm = (id: GridRowId) => () => {
    setSelectedDeleteId(id);
    setConfirmOpen(true);
  }

  const handleDeleteClick = (id: GridRowId) => {
    if(!id) {
      setConfirmOpen(false);
      setRows(rows.filter((row) => row.id !== id));
      return;
    }
    destroy(id, gridProps.master)?.then(() => {
      setRows(rows.filter((row) => row.id !== id));
      setConfirmOpen(false);
      toast.success('Record deleted', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        });

        if(gridProps.reloadParentOnEditStop) {
          dataContext.toggleReloadParent();
        }
    });
  }
  

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);

    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };
  const processRowUpdateError = (error: any) => {
    let errorData = error.response?.data.error;
    console.error('onProcessRowUpdateError', error);
    setRows(rows.filter((row) => row.id !== ''));
    toast.error(errorData || 'Update error', {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "dark",
      });
  }

  const processRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel) => {
    
    let hasChanged = false;
    Object.keys(newRow).forEach((key) => {
      let change = newRow[key]!==oldRow[key];
      if(change) {
        //Before setting hasChange check if the values are different but both are empty
        if(!newRow[key] && !oldRow[key]) {
          change = false;
        }
      }
      
      if(change) hasChanged = true;
    });

    if(newRow.errors || !hasChanged) return oldRow;
    return update(newRow, gridProps.master).then((returnRow) => {
      const oldId = newRow.id;
      const updatedRow = { ...returnRow, isNew: false};
      setRows(rows.map((row) => (row.id === oldId ? updatedRow : row)));

      toast.success('Record updated', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        });
        
        if(gridProps.reloadParentOnEditStop) {
          dataContext.toggleReloadParent();
        }

      return updatedRow;
    });
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };


  interface EditToolbarProps {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (
      newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
    ) => void;
  }
  
  function EditToolbar(props: EditToolbarProps) {
    //if(!editControls) return (<></>);
    const { setRows, setRowModesModel } = props;
  
    const handleClick = () => {
      const id = '';

      setRows((oldRows) => {
        oldRows = oldRows.filter((row) => row.id !== '');
        return [...oldRows, { id, isNew: true }]
      });
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: columns[1].field },
      }));
      let pageSize = gridProps.initialPageSize ?? 10;
      apiRef.current.setPage(rows.length/pageSize)
    };
  
    return (
      <GridToolbarContainer sx={{display: !editControls && !gridProps.showGridToolbar ? 'none' : 'block'}}>
        {gridProps.showGridToolbar && <GridToolbar />}

        <Box sx={{ display: editControls && !gridProps.hideAddButton ? 'flex' : 'none', flex: '1 1 100%', justifyContent: 'flex-end' }}>
        <Button
              variant="contained"
              color="secondary"
              size="small"
              ref={gridProps.addButtonRef}
              startIcon={<AddIcon />} 
              onClick={handleClick}
        >{gridProps.labels?.add?.button?gridProps.labels?.add.button:'Add record'}</Button>
        </Box>
      </GridToolbarContainer>
    );
  }


    const renderDetailsButton = (params:any) => {
      return (
          <strong>
              <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => {
                      //parseName(params.row.col6)
                      setDrawerOpen(true);
                      setSelectedItem(params.row);
                  }}
              >
                  {gridProps.labels?.details?.button ? gridProps.labels?.details.button : "Edit"}
              </Button>
          </strong>
      )
    }
    
    var columns : GridColDef[] = gridProps.columns;
    if(gridProps.details) {
      columns = [...columns,  {
        field: 'details',
        headerName: gridProps.labels?.details?.column ? gridProps.labels?.details.column : 'Details',
        flex: 1,
        maxWidth: 150,
        renderCell: renderDetailsButton,
      }];
    }
    if(editControls) {
      columns = [...columns, {
        field: 'actions',
        type: 'actions',
        headerName: gridProps.labels?.actions ? gridProps.labels?.actions : 'Actions',
        width: 100,
        cellClassName: 'actions',
        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
  
          if (isInEditMode) {
            return [
              <GridActionsCellItem
                icon={<CheckCircle />}
                label="Save"
                sx={{
                  color: 'primary.main',
                }}
                onClick={handleSaveClick(id)}
              />,
              <GridActionsCellItem
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];
          }
            return [
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                onClick={handleEditClick(id)}
                color="inherit"
              />,
              gridProps.hideDeleteButton ? <></> :
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={handleDeleteConfirm(id)}
                color="inherit"
              />,
            ];
        },
      }];
    }
  
    return (
      <Box sx={{ width: '100%' }}>

      <Dialog
        open={confirmOpen}
        onClose={()=>setConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
      <DialogTitle id="alert-dialog-title">
          {"Confirm item deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the selected item?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmOpen(false)}>No</Button>
          <Button onClick={()=>{handleDeleteClick(selectedDeleteId)}} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <StyledBox>
        <DataGrid
          apiRef={apiRef} 
          getRowId={(row) => row.id}
          rows={rows}
          columns={columns}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          onProcessRowUpdateError={processRowUpdateError}
          processRowUpdate={processRowUpdate}
          initialState={{
            pagination: { paginationModel: { pageSize: gridProps.initialPageSize ?? 10 } },
            filter: { filterModel: gridProps.filterModel },    
            columns: {
              columnVisibilityModel: gridProps.columnVisibility
            }      
          }}
          loading={loading}
          slots={{
            toolbar: EditToolbar,
          }}
          slotProps={{
            toolbar: { setRows, setRowModesModel },
          }}
          style={{ border: 'none' }}
        />
      </StyledBox>

    {gridProps.details &&
    <Box sx={{ width: { sm: gridProps.detailsWidth ? gridProps.detailsWidth : '70%' }, flexShrink: { sm: 0 } }}>
    <Drawer
      anchor='right'
      open={drawerOpen}
      onClose={()=>{setDrawerOpen(false)}}
      sx={{
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: gridProps.detailsWidth ? gridProps.detailsWidth : '70%',
          backgroundColor: theme => theme.palette.background.default,
          color: theme => theme.palette.text.primary,
        },
      }}
    >
      <gridProps.details row={selectedItem} master={gridProps.master} setDrawerOpen={setDrawerOpen} />
    </Drawer>
    </Box>}

      </Box>
    );
}

export default EditableGrid;